/* eslint-disable react-hooks/set-state-in-effect */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, setAuthToken, loadAuthTokenFromStorage, formatApiError } from "./api";

const AuthContext = createContext(null);

const ACTIVE_CLIENT_KEY = "co.activeClientId";
const COMBINED_VIEW_KEY = "co.combinedView";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [clientRecord, setClientRecord] = useState(null);
    const [clients, setClients] = useState([]); // all clients this portal user can access
    const [activeClientId, _setActiveClientId] = useState(() => {
        // URL override wins so an admin can open a client-preview link fresh.
        try {
            const url = new URL(window.location.href);
            const q = url.searchParams.get("preview_client_id");
            if (q) return q;
        } catch { /* ignore */ }
        try { return localStorage.getItem(ACTIVE_CLIENT_KEY) || null; } catch { return null; }
    });
    const [combinedView, _setCombinedView] = useState(() => {
        try { return localStorage.getItem(COMBINED_VIEW_KEY) === "1"; } catch { return false; }
    });
    const [loading, setLoading] = useState(true);

    const setActiveClientId = useCallback((id) => {
        _setActiveClientId(id);
        // Picking a single entity always turns combined mode OFF — the two
        // are mutually exclusive from the user's mental model.
        _setCombinedView(false);
        try {
            if (id) localStorage.setItem(ACTIVE_CLIENT_KEY, id);
            else localStorage.removeItem(ACTIVE_CLIENT_KEY);
            localStorage.setItem(COMBINED_VIEW_KEY, "0");
        } catch { /* ignore */ }
    }, []);

    const setCombinedView = useCallback((flag) => {
        _setCombinedView(!!flag);
        try {
            localStorage.setItem(COMBINED_VIEW_KEY, flag ? "1" : "0");
        } catch { /* ignore */ }
    }, []);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data.user);
            setTenant(data.tenant);
            setClientRecord(data.client);
            const list = Array.isArray(data.clients) ? data.clients : (data.client ? [data.client] : []);
            setClients(list);
            // Keep activeClientId in sync: if persisted value isn't in list, fall back to primary
            const persisted = (() => { try { return localStorage.getItem(ACTIVE_CLIENT_KEY); } catch { return null; } })();
            const valid = list.find((c) => c.id === persisted);
            if (!valid && list.length > 0) {
                _setActiveClientId(list[0].id);
                try { localStorage.setItem(ACTIVE_CLIENT_KEY, list[0].id); } catch { /* ignore */ }
            } else if (valid) {
                _setActiveClientId(valid.id);
            }
            return data;
        } catch (e) {
            setUser(null);
            setTenant(null);
            setClientRecord(null);
            setClients([]);
            setAuthToken(null);
            return null;
        }
    }, []);

    useEffect(() => {
        (async () => {
            const t = loadAuthTokenFromStorage();
            if (t) {
                await refreshMe();
            }
            setLoading(false);
        })();
    }, [refreshMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setAuthToken(data.token);
        setUser(data.user);
        setTenant(data.tenant);
        await refreshMe();
        return data;
    };

    const signupFirm = async (payload) => {
        const { data } = await api.post("/auth/signup-firm", payload);
        setAuthToken(data.token);
        setUser(data.user);
        setTenant(data.tenant);
        await refreshMe();
        return data;
    };

    const signupOwner = async (payload) => {
        const { data } = await api.post("/auth/signup-owner", payload);
        setAuthToken(data.token);
        setUser(data.user);
        setTenant(data.tenant);
        await refreshMe();
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
        setAuthToken(null);
        setUser(null);
        setTenant(null);
        setClientRecord(null);
        setClients([]);
        try { localStorage.removeItem(ACTIVE_CLIENT_KEY); } catch { /* ignore */ }
        try { localStorage.setItem(COMBINED_VIEW_KEY, "0"); _setCombinedView(false); } catch { /* ignore */ }
    };

    // Derived: the currently selected client record (for portal display & uploads)
    const activeClient = clients.find((c) => c.id === activeClientId) || clientRecord;

    return (
        <AuthContext.Provider
            value={{
                user, tenant, clientRecord, clients, activeClient, activeClientId,
                setActiveClientId, combinedView, setCombinedView,
                loading, login, signupFirm, signupOwner, logout, refreshMe,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export function RequireAuth({ children, roles }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
                Loading…
            </div>
        );
    }
    if (!user) {
        // Single entry point for everyone — server decides client vs staff after email.
        return <Navigate to="/login" state={{ from: loc }} replace />;
    }
    if (roles && roles.length && !roles.includes(user.role)) {
        // Redirect to appropriate root
        if (user.role === "client") return <Navigate to="/portal" replace />;
        return <Navigate to="/app" replace />;
    }
    return children;
}

export { formatApiError };
